// Ventas · piso completo del Portal Admin VL: filtros | pastillas + barra + tabla | detalle.
// La página es la única dueña del estado; los componentes de `components/` son controlados.
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt } from 'lucide-react';
import dayjs from 'dayjs';
import { MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { ScreenFrame } from '@/shared/patterns/ScreenFrame';
import { UnavailableState } from '@/shared/patterns/DataTable';
import {
    LoadMoreButton,
    SALES_NOUN,
    footerCountLabel,
    nextPageFailureNote,
    useLoadedPaginationTable,
    type LoadedScope,
} from '@/shared/patterns/LoadedScope';
import { Button } from '@/kit';
import { useStoredFlag } from '@/shared/hooks/useStoredFlag';
import type { BackofficeSalesFilters } from '@/api/services/retail-wizard/contract';
import {
    useVenta,
    useVentaLog,
    useVentas,
    useVentasDayKpis,
    useVentasStats,
} from './api/useVentas';
import { EMPTY_FILTERS, filterByQuery } from './lib/ventasFilters';
import { useTiendas } from '../admin/tiendas/api/useTiendas';
import { networkGoal } from '../admin/home/lib/homeMetrics';
import { resolveOwnRetailerId, resolveRetailerScope } from './lib/retailerScope';
import { useAuth } from '@/hooks/useAuth';
import { salesToCsv } from './lib/exportCsv';
import { VentasFilters } from './components/VentasFilters';
import { VentasStatsBar } from './components/VentasStatsBar';
import {
    DEFAULT_SORT,
    VentasTable,
    sortSales,
    useProviderLabelOf,
    type VentasSort,
} from './components/VentasTable';
import { VentasToolbar } from './components/VentasToolbar';
import { VentaDetail } from './components/VentaDetail';
import type { SaleDetailStatus } from './lib/cancelAvailability';
import { usePromotionSaleSlots } from '@/domains/retail/promotions-wizard';

/** Llave con la que se recuerda si el operador colapsó la columna de filtros. */
const FILTERS_COLLAPSED_KEY = 'retail-ventas:filters-collapsed';

/** Descarga el CSV; el BOM hace que Excel en es-MX respete los acentos de los nombres. */
function downloadCsv(fileName: string, csv: string): void {
    const url = URL.createObjectURL(
        new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
}

/**
 * `initialFilters` siembra los filtros de los deep links (`?tienda=…`, `?vendedor=…`) y
 * `initialSaleId` abre la venta de `?venta=`. Los dos son estado inicial, no valores controlados.
 * `scopeToOwnRetailer` (portal Admin VL) fija la cadena LEVEL_1 aunque el usuario también sea TENANT.
 */
export function VentasPage({
    initialFilters,
    initialSaleId,
    scopeToOwnRetailer = false,
}: {
    initialFilters?: Partial<BackofficeSalesFilters>;
    initialSaleId?: string;
    scopeToOwnRetailer?: boolean;
} = {}) {
    const { t } = useTranslation('ventas');

    // El usuario LEVEL_1 tiene su cadena fija desde la sesión; quien abarca el silo la elige.
    const { roleAssignments } = useAuth();
    const fixedRetailerId = scopeToOwnRetailer
        ? resolveOwnRetailerId(roleAssignments)
        : resolveRetailerScope(roleAssignments).fixedRetailerId;

    const [filters, setFiltersState] = useState<BackofficeSalesFilters>({
        ...EMPTY_FILTERS,
        ...initialFilters,
    });
    const [pickedId, setPickedId] = useState<string | null>(initialSaleId ?? null);
    const [sort, setSort] = useState<VentasSort | null>(DEFAULT_SORT);
    const [filtersCollapsed, setFiltersCollapsed] = useStoredFlag(FILTERS_COLLAPSED_KEY);

    /** Los filtros que viajan al API: la cadena fija del rol se completa sola, derivada. */
    const effectiveFilters = useMemo(
        () =>
            filters.retailerId === '' && fixedRetailerId
                ? { ...filters, retailerId: fixedRetailerId }
                : filters,
        [filters, fixedRetailerId],
    );

    const noRetailer = effectiveFilters.retailerId === '';
    const ventas = useVentas(effectiveFilters);
    const ventasStats = useVentasStats(effectiveFilters);
    const dayKpis = useVentasDayKpis(!noRetailer);
    const stores = useTiendas('today', { rosterOptional: true });
    const dayGoal = useMemo(
        () =>
            stores.error || stores.goalsError || !stores.data
                ? undefined
                : networkGoal(stores.data.items, stores.data.activeStoreIds),
        [stores.error, stores.goalsError, stores.data],
    );
    const promotionSlots = usePromotionSaleSlots('ADMIN', effectiveFilters.retailerId);

    const loadedSales = useMemo(
        () => ventas.data?.pages.flatMap((p) => p.items) ?? [],
        [ventas.data],
    );

    /** Lo que la tabla muestra: lo cargado menos lo que no coincide con la búsqueda en cliente. */
    const sales = useMemo(
        () => filterByQuery(loadedSales, filters.query),
        [loadedSales, filters.query],
    );

    const listLoading = ventas.isPending && !noRetailer;
    // Falló la PRIMERA lectura: sin filas no hay lista. Si falla una página siguiente, lo cargado se queda.
    const readFailed = ventas.isError && ventas.data === undefined;
    const nextPageError =
        ventas.isFetchNextPageError && ventas.error instanceof Error ? ventas.error.message : null;
    const hasList = !noRetailer && !listLoading && !readFailed;

    const providerLabelOf = useProviderLabelOf();
    const sortedSales = useMemo(
        () => sortSales(sales, sort, providerLabelOf),
        [sales, sort, providerLabelOf],
    );
    const table = useLoadedPaginationTable(sortedSales);
    const modelRows = table.getRowModel().rows;
    const pageRows = useMemo(() => modelRows.map((row) => row.original), [modelRows]);
    const scope: LoadedScope | null = hasList
        ? { loaded: loadedSales.length, hasMore: ventas.hasNextPage }
        : null;

    // Sin elección del operador, el detalle abre la primera venta de la lista, como el mock.
    const firstSale = sortedSales.length > 0 ? sortedSales[0] : null;
    const selectedId = pickedId ?? firstSale?.saleId ?? null;
    const detail = useVenta(selectedId);
    const saleLog = useVentaLog(selectedId);
    const listedSale = loadedSales.find((s) => s.saleId === selectedId) ?? null;
    const selectedSale = detail.data ?? listedSale;
    // La lista no trae `cancellation`: cancelar espera al detalle.
    const detailStatus: SaleDetailStatus = detail.data
        ? 'success'
        : detail.isError
          ? 'error'
          : 'pending';

    // Cambiar los filtros cambia el universo: quedarse en la página 4 mostraría un hueco vacío.
    const setFilters = useCallback(
        (next: BackofficeSalesFilters) => {
            setFiltersState(next);
            table.setPageIndex(0);
        },
        [table],
    );

    const unavailableState = noRetailer ? (
        <UnavailableState
            icon={<Receipt className="mb-3 size-9 text-muted-foreground/40" aria-hidden />}
            title={t('pagina.sinCadenaTitulo')}
            body={t('pagina.sinCadenaDescripcion')}
        />
    ) : (
        <UnavailableState
            title={t('pagina.errorTitulo')}
            body={
                ventas.error instanceof Error ? ventas.error.message : t('pagina.errorDescripcion')
            }
            action={
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                        void ventas.refetch();
                    }}
                >
                    {t('pagina.reintentar')}
                </Button>
            }
        />
    );

    const pageCount = Math.max(1, table.getPageCount());

    return (
        <ScreenFrame>
            <MasterDetailExplorer
                storageKey="retail-ventas"
                filtersCollapsed={filtersCollapsed}
                filters={
                    <VentasFilters
                        filters={effectiveFilters}
                        onChange={setFilters}
                        loadedCount={loadedSales.length}
                        hasMore={ventas.hasNextPage}
                        retailerFixed={!!fixedRetailerId}
                        canExport={hasList && sales.length > 0}
                        onExport={() => {
                            downloadCsv(
                                `ventas-${dayjs().format('YYYY-MM-DD')}.csv`,
                                salesToCsv(sales),
                            );
                        }}
                        collapsed={filtersCollapsed}
                        onToggleCollapsed={() => {
                            setFiltersCollapsed(!filtersCollapsed);
                        }}
                    />
                }
                list={
                    <>
                        <VentasStatsBar
                            sales={sales}
                            serverStats={ventasStats.data}
                            filters={effectiveFilters}
                            onChange={setFilters}
                            available={hasList}
                            allLoaded={hasList && !ventas.hasNextPage}
                        />
                        <VentasToolbar
                            sales={hasList ? sales : null}
                            scope={scope}
                            dayKpis={dayKpis.data}
                            dayGoal={dayGoal}
                            dayGoalLoading={stores.isPending}
                            statusLabel={
                                noRetailer
                                    ? undefined
                                    : footerCountLabel(null, null, SALES_NOUN, readFailed)
                            }
                            pager={
                                hasList
                                    ? {
                                          page: table.getState().pagination.pageIndex + 1,
                                          pageCount,
                                          onPrevious: () => {
                                              table.previousPage();
                                          },
                                          onNext: () => {
                                              table.nextPage();
                                          },
                                      }
                                    : undefined
                            }
                        />
                        <VentasTable
                            sales={noRetailer || readFailed ? null : pageRows}
                            unavailable={unavailableState}
                            isLoading={listLoading}
                            sortedCount={sortedSales.length}
                            selectedId={selectedId}
                            onSelect={setPickedId}
                            sort={sort}
                            onSortChange={setSort}
                            hasMore={ventas.hasNextPage}
                        />
                        {/* El fin de la lista lo decide `nextToken`, nunca la cantidad de ítems. */}
                        {hasList && ventas.hasNextPage ? (
                            <div className="flex flex-none flex-col items-center gap-1 border-t border-border px-3 py-1.5">
                                {nextPageError !== null ? (
                                    <p
                                        role="alert"
                                        className="text-center text-[10.5px] text-danger"
                                    >
                                        {nextPageFailureNote(nextPageError, SALES_NOUN)}
                                    </p>
                                ) : null}
                                <LoadMoreButton
                                    noun={SALES_NOUN}
                                    isLoading={ventas.isFetchingNextPage}
                                    hasFailed={nextPageError !== null}
                                    onLoadMore={() => {
                                        void ventas.fetchNextPage();
                                    }}
                                />
                            </div>
                        ) : null}
                    </>
                }
                detail={
                    noRetailer ? null : (
                        <VentaDetail
                            sale={selectedSale}
                            log={saleLog.data}
                            logFailed={saleLog.isError}
                            slots={promotionSlots}
                            retailerId={effectiveFilters.retailerId}
                            detailStatus={detailStatus}
                            isLoading={
                                selectedId !== null && selectedSale === null && detail.isFetching
                            }
                        />
                    )
                }
            />
        </ScreenFrame>
    );
}
